import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { NavLink, useSearchParams } from "react-router-dom";
import PageHeading from "@/v2/components/ui/heading/Page";
import { DataTable, TableToolbar } from "@/v2/components/data-table/table";
import { Payslip } from "@/v2/types";
import AccessControl from "@/v2/components/rbac/AccessControl";
import FindAllPayslip from "@/v2/api/payslip/FindAll";
import parseUrlToQueryParams from "@/v2/utils/parseUrlToQueryParams";

import {
    ArOfficeStaffPayslipColumn,
    ArOfficeStaffPayslipFilterColumn,
} from "@/v2/components/data-table/columns/ArOfficeStaffPayslipColumn";
import { hasRole } from "@/v2/utils/hasRole";
import { useAppSelector } from "@/v2/hooks/hooks";
import { UTCDate } from "@date-fns/utc";
import { format } from "date-fns-tz";
import { useState } from "react";
import MonthYearPicker from "@/v2/components/ui/MonthPicker";

export default function ArOfficeStaffPayslipPage() {
    const [searchParams, _setSearchParams] = useSearchParams();
    const queryParams = parseUrlToQueryParams(searchParams);

    const user = useAppSelector((state) => state.user.value);

    const isAdmin = hasRole(user, [
        "MANAGING_DIRECTOR",
        "HR_ADMINISTRATION_OFFICER",
    ]);

    if (!isAdmin) {
        return null;
    }

    const now = new UTCDate();
    const currentMonth = format(now, "yyyy-MM");
    const appliedMonth = searchParams.get("month");

    const [month, setMonth] = useState(appliedMonth || currentMonth);

    const monthNumber = Number(month.split("-")[1]);

    const { skip, limit, search, filter } = queryParams;

    const { refetch, ...query } = useQuery({
        queryKey: ["payslip", skip, limit, search, filter, month],
        queryFn: () =>
            FindAllPayslip({
                limit: limit ?? 20,
                skip: skip ?? 0,
                search: search,
                filter: {
                    ...filter,
                    type: { eq: ["MONTHLY_STAFF"] },
                    month: { eq: monthNumber },
                },
            }),
    });

    return (
        <>
            <div className="flex flex-wrap items-center justify-between gap-4 py-2">
                <PageHeading
                    title="AR Office Staff Payslip Database"
                    description="Manage and monitor all office staff payslip in your platform"
                />

                <div className="flex items-center justify-center gap-2">
                    <MonthYearPicker month={month} setMonth={setMonth} />
                    <AccessControl
                        module="PAYSLIP_MANAGEMENT"
                        requestedAccess={"POST"}
                    >
                        <div className="flex gap-4">
                            <NavLink to={"create"}>
                                <Button>Add New AR Office Staff Payslip</Button>
                            </NavLink>
                        </div>
                    </AccessControl>
                </div>
            </div>

            <Card className="p-2">
                <TableToolbar<Payslip>
                    column={ArOfficeStaffPayslipFilterColumn}
                    searchPlaceHolder="Search payslip.."
                />

                <DataTable<Payslip>
                    data={query.data?.data?.payslips || []}
                    columns={ArOfficeStaffPayslipColumn}
                    loading={query.isLoading || query.isFetching}
                    pagination={query?.data?.pagination!}
                />
            </Card>
        </>
    );
}
